import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Crosshair, Clock, AlertTriangle, ArrowUpRight, Users, Megaphone, Briefcase, Network, Eye, TrendingUp, Radio } from 'lucide-react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { RelatedWorkflow } from '../components/dashboard/RelatedWorkflow';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
const narratives = [{
  id: 1,
  title: 'Konspirasi vaksin ulang massal',
  stage: 'EMERGING',
  velocity: 245,
  sources: ['Telegram @indo_aware', 'TikTok @cek_fakta_'],
  tta: '6-12 jam',
  risk: 'TINGGI',
  mentions: 4200,
  data: Array.from({
    length: 10
  }, (_, i) => ({
    v: i * i + 5
  }))
}, {
  id: 2,
  title: 'Hoaks penarikan dana haji',
  stage: 'GROWING',
  velocity: 180,
  sources: ['WhatsApp Channels', 'FB Group Komunitas'],
  tta: '3-6 jam',
  risk: 'KRITIS',
  mentions: 8900,
  data: Array.from({
    length: 10
  }, (_, i) => ({
    v: i * 5 + 10
  }))
}, {
  id: 3,
  title: 'Tudingan kebocoran data Dukcapil',
  stage: 'PEAK-WATCH',
  velocity: 95,
  sources: ['X @leakwatch_id'],
  tta: '> 24 jam',
  risk: 'SEDANG',
  mentions: 2100,
  data: Array.from({
    length: 10
  }, (_, i) => ({
    v: Math.sin(i) * 5 + 10
  }))
}, {
  id: 4,
  title: 'Narasi pengembalian beras impor',
  stage: 'EMERGING',
  velocity: 320,
  sources: ['TikTok komunitas petani'],
  tta: '4-8 jam',
  risk: 'TINGGI',
  mentions: 5400,
  data: Array.from({
    length: 10
  }, (_, i) => ({
    v: i * 3 + 8
  }))
}, {
  id: 5,
  title: 'Tuduhan KKN proyek IKN',
  stage: 'GROWING',
  velocity: 140,
  sources: ['YouTube podcast politik'],
  tta: '12-24 jam',
  risk: 'SEDANG',
  mentions: 3300,
  data: Array.from({
    length: 10
  }, (_, i) => ({
    v: i * 2 + 6
  }))
}];
const stageColor: Record<string, string> = {
  EMERGING: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  GROWING: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  'PEAK-WATCH': 'bg-red-500/10 text-red-500 border-red-500/20'
};
const riskColor: Record<string, string> = {
  KRITIS: 'text-red-500',
  TINGGI: 'text-amber-500',
  SEDANG: 'text-blue-500'
};
export function ShadowNarrative() {
  const [selectedNarrative, setSelectedNarrative] = useState<(typeof narratives)[number] | null>(null);
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-3383">
      <PageHeader group="Radar Isu" title="Shadow Narrative Detector" description="Deteksi narasi tersembunyi sebelum viral. Sistem mengidentifikasi sinyal kecil yang berpotensi menjadi krisis." icon={Crosshair} data-id="element-3384" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3385">
        {[{
        label: 'Narasi Terdeteksi',
        value: '23',
        color: 'text-amber-500'
      }, {
        label: 'Potensi Viral 24J',
        value: '5',
        color: 'text-red-500'
      }, {
        label: 'Total Mentions',
        value: '24.1K',
        color: 'text-blue-500'
      }].map(k => <Card key={k.label} className="bg-card border-border/50" data-id="element-3386">
            <CardContent className="p-4" data-id="element-3387">
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-3388">
                {k.label}
              </p>
              <p className={`font-heading text-2xl font-bold mt-1 ${k.color}`} data-id="element-3389">
                {k.value}
              </p>
            </CardContent>
          </Card>)}
      </div>

      <div className="flex flex-col gap-4" data-id="element-3390">
        {narratives.map(n => <Card key={n.id} className="bg-card border-border/50 hover:bg-muted/30 transition" data-id="element-3391">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-3392">
              <div className="size-12 rounded bg-muted/30 border border-border/50 flex items-center justify-center shrink-0" data-id="element-3393">
                <AlertTriangle className={`size-6 ${riskColor[n.risk]}`} data-id="element-3394" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-3395">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3396">
                  <h3 className="text-sm font-bold" data-id="element-3397">{n.title}</h3>
                  <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border ${stageColor[n.stage]}`} data-id="element-3398">
                    {n.stage}
                  </span>
                  <span className={`font-mono text-[9px] font-bold ${riskColor[n.risk]}`} data-id="element-3399">
                    RISK: {n.risk}
                  </span>
                </div>
                <div className="flex items-center gap-4 font-mono text-[10px] text-muted-foreground flex-wrap" data-id="element-3400">
                  <span data-id="element-3401">
                    <span className="text-foreground" data-id="element-3402">
                      {n.mentions.toLocaleString('id-ID')}
                    </span>{' '}
                    mentions
                  </span>
                  <span data-id="element-3403">
                    Velocity:{' '}
                    <span className="text-amber-500" data-id="element-3404">+{n.velocity}%/jam</span>
                  </span>
                  <span data-id="element-3405">
                    <Clock className="size-3 inline mr-1" data-id="element-3406" />
                    TTA: {n.tta}
                  </span>
                  <span data-id="element-3407">Sumber: {n.sources.join(', ')}</span>
                </div>
              </div>

              <div className="w-32 h-12 shrink-0 opacity-60" data-id="element-3408">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3409">
                  <AreaChart data={n.data} data-id="element-3410">
                    <Area type="monotone" dataKey="v" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} data-id="element-3411" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-col gap-1 shrink-0" data-id="element-3412">
                <Button size="sm" variant="default" onClick={() => toast.warning('Narasi dieskalasi ke pimpinan pengamanan')} data-id="element-3413">
                  Eskalasi <ArrowUpRight className="size-3 ml-1" data-id="element-3414" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedNarrative(n)} data-id="element-3415">
                  <Eye className="size-3 mr-1" data-id="element-3416" />
                  Detail
                </Button>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedNarrative} onOpenChange={o => !o && setSelectedNarrative(null)} data-id="element-3417">
        <DialogContent className="sm:max-w-[640px] border-border/50 bg-background/95 backdrop-blur" data-id="element-3418">
          {selectedNarrative && <>
              <DialogHeader data-id="element-3419">
                <div className="flex items-start gap-3" data-id="element-3420">
                  <div className="size-10 rounded bg-muted/40 border border-border/50 flex items-center justify-center shrink-0" data-id="element-3421">
                    <AlertTriangle className={`size-5 ${riskColor[selectedNarrative.risk]}`} data-id="element-3422" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3423">
                    <DialogTitle className="text-base font-bold leading-tight" data-id="element-3424">
                      {selectedNarrative.title}
                    </DialogTitle>
                    <DialogDescription className="text-xs mt-1 flex items-center gap-2 flex-wrap" data-id="element-3425">
                      <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border ${stageColor[selectedNarrative.stage]}`} data-id="element-3426">
                        {selectedNarrative.stage}
                      </span>
                      <span className={`font-mono text-[9px] font-bold ${riskColor[selectedNarrative.risk]}`} data-id="element-3427">
                        RISK: {selectedNarrative.risk}
                      </span>
                      <span className="font-mono text-[9px] text-muted-foreground" data-id="element-3428">
                        ID #{selectedNarrative.id}
                      </span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-2" data-id="element-3429">
                {/* Metrics grid */}
                <div className="grid grid-cols-3 gap-2" data-id="element-3430">
                  <div className="p-3 rounded border border-border/50 bg-muted/20" data-id="element-3431">
                    <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-3432">
                      Mentions
                    </p>
                    <p className="font-heading text-lg font-bold mt-0.5" data-id="element-3433">
                      {selectedNarrative.mentions.toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="p-3 rounded border border-border/50 bg-muted/20" data-id="element-3434">
                    <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-3435">
                      Velocity
                    </p>
                    <p className="font-heading text-lg font-bold mt-0.5 text-amber-500" data-id="element-3436">
                      +{selectedNarrative.velocity}%/j
                    </p>
                  </div>
                  <div className="p-3 rounded border border-border/50 bg-muted/20" data-id="element-3437">
                    <p className="font-mono text-[9px] uppercase text-muted-foreground" data-id="element-3438">
                      TTA
                    </p>
                    <p className="font-heading text-sm font-bold mt-1" data-id="element-3439">
                      {selectedNarrative.tta}
                    </p>
                  </div>
                </div>

                {/* Predicted reach */}
                <div className="p-3 rounded border border-amber-500/30 bg-amber-500/5" data-id="element-3440">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3441">
                    <TrendingUp className="size-3.5 text-amber-500" data-id="element-3442" />
                    <p className="font-mono text-[10px] uppercase tracking-wider text-amber-700" data-id="element-3443">
                      Prediksi Reach 24 Jam
                    </p>
                  </div>
                  <p className="font-heading text-2xl font-bold text-amber-600" data-id="element-3444">
                    {Math.round(selectedNarrative.mentions * (1 + selectedNarrative.velocity / 100) * 6).toLocaleString('id-ID')}
                  </p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-0.5" data-id="element-3445">
                    Berbasis velocity saat ini & pola amplifikasi historis.
                  </p>
                </div>

                {/* Sources */}
                <div data-id="element-3446">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5" data-id="element-3447">
                    <Radio className="size-3" data-id="element-3448" />
                    Sumber Terdeteksi
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-3449">
                    {selectedNarrative.sources.map(s => <Badge key={s} variant="outline" className="font-mono text-[10px]" data-id="element-3450">
                        {s}
                      </Badge>)}
                  </div>
                </div>

                {/* Recommended actions */}
                <div data-id="element-3451">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2" data-id="element-3452">
                    Rekomendasi Aksi
                  </p>
                  <ul className="space-y-1.5" data-id="element-3453">
                    {['Kirim kontra-narasi via Juru Bicara Kepresidenan dalam 6 jam', 'Tag akun amplifier untuk monitoring intensif', 'Koordinasi dengan platform host untuk takedown bila melanggar ToS', 'Brief K/L terkait untuk klarifikasi data resmi'].map((a, i) => <li key={i} className="flex items-start gap-2 text-xs" data-id="element-3454">
                        <span className="font-mono text-[10px] text-primary mt-0.5" data-id="element-3455">
                          {i + 1}.
                        </span>
                        <span className="text-muted-foreground leading-relaxed" data-id="element-3456">
                          {a}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-2" data-id="element-3457">
                <Button variant="outline" size="sm" onClick={() => setSelectedNarrative(null)} data-id="element-3458">
                  Tutup
                </Button>
                <Button size="sm" onClick={() => {
              toast.success(`"${selectedNarrative.title}" ditambahkan ke watchlist`);
              setSelectedNarrative(null);
            }} data-id="element-3459">
                  <Eye className="size-3 mr-1" data-id="element-3460" />
                  Tambah ke Watchlist
                </Button>
              </DialogFooter>
            </>}
        </DialogContent>
      </Dialog>

      <RelatedWorkflow currentStep="Analisis Narasi Bayangan" nextSteps={[{
      to: '/actor-network',
      icon: Users,
      label: 'Lihat Aktor Penyebar',
      desc: 'Identifikasi origin & amplifier narasi.'
    }, {
      to: '/response-recommender',
      icon: Megaphone,
      label: 'Generate Kontra-Narasi',
      desc: 'Buat respons resmi multi-format.'
    }, {
      to: '/command-tasking',
      icon: Briefcase,
      label: 'Tasking Klarifikasi',
      desc: 'Kirim instruksi ke Juru Bicara/K/L.'
    }, {
      to: '/hybrid-threats',
      icon: Network,
      label: 'Cek Korelasi Hybrid',
      desc: 'Apakah ini bagian dari serangan multi-vektor?'
    }]} data-id="element-3461" />
    </div>;
}